const ATTRIBUTE_TOOLTIP = 'data-klt-c-tooltip';
const ATTRIBUTE_TOOLTIP_CONTROLLED_BY = 'data-klt-c-tooltip-controlled-by';

const CLASS_INPUT_TOOLTIP_INITALIZED = 'klt-c-form-field-tooltip--js';
const CLASS_TOOLTIP_INITALIZED = 'klt-c-tooltip--initalized';
const CLASS_TOOLTIP_VISIBLE = 'klt-c-tooltip--visible';
const CLASS_TOOLTIP_TEMPORARY_HIDDEN = 'klt-c-tooltip--temporary-hidden';

const tooltips: {
    tooltipElement: HTMLElement;
    controlElement: HTMLElement;
}[] = [];
let scrollHandlerActive = false;

/**
 * Inits JavaScript enhancements for form fields
 */
export default function init() {
    try {
        initTooltips();

        if (!scrollHandlerActive) {
            initPositionHandlers();
            scrollHandlerActive = true;
        }
    } catch (e) {
        console.error(`Couldn't initalize tooltips module.`, e);
    }
}

/**
 * Adds interactive tooltip functionality to form inputs with a specific attribute.
 * TODO: A11y: Add aria [aria-hidden="true"] handling
 * TODO: A11y: Bind ESC to close tooltip
 */
export function initTooltips() {
    const tooltipElements = document.querySelectorAll(`[${ATTRIBUTE_TOOLTIP}]`);

    if (tooltipElements.length > 0) {
        for (let i = 0; i < tooltipElements.length; i++) {
            const tooltipElement = tooltipElements[i] as HTMLElement;

            const controlledBy = (tooltipElement as HTMLElement).getAttribute(`${ATTRIBUTE_TOOLTIP_CONTROLLED_BY}`);
            if (!controlledBy) {
                continue;
            }

            const controlElement = document.getElementById(`${controlledBy}`);
            if (!controlElement) {
                continue;
            }

            // Remove optimizations for non-javascript environments
            controlElement.removeAttribute('title');
            controlElement.removeAttribute('disabled');

            tooltips.push({ tooltipElement, controlElement });
            alignTooltip(controlElement, tooltipElement);

            tooltipElement.classList.add(`${CLASS_TOOLTIP_INITALIZED}`);

            const enterHandler = (event: Event) => {
                const target = event.currentTarget as HTMLElement;

                alignTooltip(target, tooltipElement);
                showTooltip(tooltipElement);

                event.stopPropagation();
                event.preventDefault();
            };

            const leaveHandler = (event: Event) => {
                if (event.type === 'mouseleave' && document.activeElement === event.currentTarget) {
                    return;
                }

                hideTooltip(tooltipElement);
            };

            if (['button', 'input'].includes(controlElement.nodeName.toLowerCase())) {
                controlElement.addEventListener('focus', enterHandler);
                controlElement.addEventListener('mouseenter', enterHandler);
                controlElement.addEventListener('mouseleave', leaveHandler);
                controlElement.addEventListener('blur', leaveHandler);
            }
        }
    }
}

function alignTooltip(target: HTMLElement, tooltipElement: HTMLElement) {
    const pointerSpacingToElement = 0;
    const position = target.getBoundingClientRect();
    const centerDelta = tooltipElement.offsetWidth / 2 - target.offsetWidth / 2;
    const tooltipX = position.left - centerDelta;
    const tooltipY = position.top - tooltipElement.offsetHeight - pointerSpacingToElement;
    const targetWidthCenter = target.offsetWidth;

    tooltipElement.style.left = `${tooltipX}px`;
    tooltipElement.style.top = `${tooltipY}px`;
}

function hideAllTooltips() {
    for (let tooltip of tooltips) {
        hideTooltip(tooltip.tooltipElement);
    }
}

function showTooltip(tooltip: HTMLElement) {
    tooltip.classList.add(`${CLASS_TOOLTIP_VISIBLE}`);
}

function hideTooltip(tooltip: HTMLElement) {
    tooltip.classList.remove(`${CLASS_TOOLTIP_VISIBLE}`);
}

function initPositionHandlers() {
    let omitHiding = false;

    const hideTooltipOnScroll = () => {
        if (!omitHiding) {
            omitHiding = true;
            for (const tooltip of tooltips) {
                tooltip.tooltipElement.classList.add(`${CLASS_TOOLTIP_TEMPORARY_HIDDEN}`);
            }
        }
    };

    const showTooltipAfterScroll = debounce(() => {
        for (const tooltip of tooltips) {
            tooltip.tooltipElement.classList.remove(`${CLASS_TOOLTIP_TEMPORARY_HIDDEN}`);
            alignTooltip(tooltip.controlElement, tooltip.tooltipElement);
        }

        omitHiding = false;
    }, 500);

    window.addEventListener('scroll', hideTooltipOnScroll);
    window.addEventListener('scroll', showTooltipAfterScroll);
    window.addEventListener('resize', hideTooltipOnScroll);
    window.addEventListener('resize', showTooltipAfterScroll);
}

function debounce(func: Function, wait: number, immediate?: boolean) {
    var timeout;
    return function () {
        var context = this,
            args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}
